import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Box, Text, Group, SimpleGrid, Skeleton, Stack, Badge, ScrollArea, Center } from '@mantine/core'
import { useBreakpoint } from '../hooks/useBreakpoint'
import {
  IconTicket, IconBottle, IconFlame, IconTool, IconBrandLine, IconWorld,
} from '@tabler/icons-react'
import { getWorkOrders, getHkOrders } from '../api/client'
import OrderSummarySection from '../components/facility/OrderSummarySection'

const STATUS_COLOR: Record<string, string> = { OPEN: 'orange', IN_PROGRESS: 'blue', DONE: 'lime', CANCELLED: 'gray' }

function StatCard({ label, value, icon: Icon, color, sub, onClick }: {
  label: string; value: any; icon: any; color: string; sub?: string; onClick?: () => void
}) {
  return (
    <Box onClick={onClick}
      style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', padding: '20px 16px 16px', cursor: onClick ? 'pointer' : 'default', transition: 'border-color 0.15s', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}
      onMouseEnter={e => { if (onClick) (e.currentTarget as HTMLElement).style.borderColor = color }}
      onMouseLeave={e => { if (onClick) (e.currentTarget as HTMLElement).style.borderColor = '#e2e8f0' }}
    >
      <Box style={{ width: 60, height: 60, borderRadius: '50%', background: color + '15', border: `2px solid ${color}40`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Icon size={26} color={color} />
      </Box>
      {value !== undefined ? (
        <Text style={{ fontFamily: 'Inter, sans-serif', fontSize: 28, fontWeight: 700, color: '#0f172a', lineHeight: 1, letterSpacing: '-0.5px' }}>
          {value}
        </Text>
      ) : (
        <Skeleton width={60} height={32} />
      )}
      <Text style={{ fontSize: 11, color: '#94a3b8', fontWeight: 500, letterSpacing: '0.3px', textTransform: 'uppercase', textAlign: 'center' }}>
        {label}
      </Text>
      {sub && <Text size="xs" c="dimmed" ta="center">{sub}</Text>}
    </Box>
  )
}

function OpenListCard({ title, items, emptyText, icon: Icon, onNavigate }: {
  title: string; items: any[] | null; emptyText: string; icon: any; onNavigate: () => void
}) {
  return (
    <Box style={{ background: '#fff', borderRadius: 12, padding: '20px 24px', border: '1px solid #e2e8f0' }}>
      <Group justify="space-between" mb={12}>
        <Group gap={8}>
          <Icon size={16} color="#0f172a" />
          <Text style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>{title}</Text>
        </Group>
        <Text size="xs" style={{ color: '#0d9488', cursor: 'pointer', fontWeight: 500 }} onClick={onNavigate}>ดูทั้งหมด →</Text>
      </Group>
      {items === null ? (
        <Skeleton height={160} />
      ) : items.length === 0 ? (
        <Center py={30}><Text size="sm" c="dimmed">{emptyText}</Text></Center>
      ) : (
        <ScrollArea h={220}>
          <Stack gap={6}>
            {items.slice(0, 15).map((w: any) => (
              <Box key={w.id} onClick={onNavigate} style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px',
                background: '#f8fafc', borderRadius: 8, cursor: 'pointer',
                border: '1px solid #e2e8f0',
              }}>
                {w.isUrgent && <IconFlame size={14} color="#ef4444" style={{ flexShrink: 0 }} />}
                {w.source === 'LINE'
                  ? <IconBrandLine size={14} color="#06C755" style={{ flexShrink: 0 }} />
                  : <IconWorld size={14} color="#94a3b8" style={{ flexShrink: 0 }} />}
                <Box style={{ flex: 1, minWidth: 0 }}>
                  <Text size="xs" fw={600} lineClamp={1} style={{ color: '#0f172a' }}>#{w.id} {w.title}</Text>
                  {w.location && <Text size="xs" style={{ color: '#64748b' }}>📍 {w.location}</Text>}
                </Box>
                <Badge size="xs" color={STATUS_COLOR[w.status]} variant="light" style={{ flexShrink: 0 }}>
                  {w.status}
                </Badge>
              </Box>
            ))}
          </Stack>
        </ScrollArea>
      )}
    </Box>
  )
}

export default function FacilityDashboardPage() {
  const { isMobile, isSmall } = useBreakpoint()
  const navigate = useNavigate()
  const { t } = useTranslation()

  const [workOrders, setWorkOrders] = useState<any[] | null>(null)
  const [hkOrders, setHkOrders]     = useState<any[] | null>(null)

  useEffect(() => {
    getWorkOrders().then(r => setWorkOrders(r.data ?? [])).catch(() => setWorkOrders([]))
    getHkOrders().then(r => setHkOrders(r.data ?? [])).catch(() => setHkOrders([]))
  }, [])

  const woOpen = (workOrders ?? []).filter(w => w.status === 'OPEN' || w.status === 'IN_PROGRESS')
  const woUrgent = woOpen.filter(w => w.isUrgent)
  const woInProgress = woOpen.filter(w => w.status === 'IN_PROGRESS')
  const hkoOpen = (hkOrders ?? []).filter(w => w.status === 'OPEN' || w.status === 'IN_PROGRESS')
  const hkoUrgent = hkoOpen.filter(w => w.isUrgent)
  const hkoInProgress = hkoOpen.filter(w => w.status === 'IN_PROGRESS')

  return (
    <Box p={isSmall ? 16 : 32}>
      <Box mb={isMobile ? 'md' : 'xl'}>
        <Text style={{ fontFamily: 'Inter, sans-serif', fontSize: isSmall ? 20 : 24, fontWeight: 600, letterSpacing: '-0.3px', color: '#0f172a' }}>
          {t('facility.title', 'แดชบอร์ดออเดอร์')}
        </Text>
        <Text size="sm" c="dimmed">{t('facility.subtitle', 'ภาพรวมงานแจ้งซ่อม (Work Order) และคำขอของใช้ (HK Order)')}</Text>
      </Box>

      <Text style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, letterSpacing: '0.5px', textTransform: 'uppercase', marginBottom: 10 }}>
        {t('facility.sectionWorkOrder', 'Work Orders')}
      </Text>
      <SimpleGrid cols={{ base: 2, md: 4 }} mb="xl">
        <StatCard label={t('workOrders.statusOpen')} value={workOrders === null ? undefined : woOpen.length} icon={IconTicket} color="#f59e0b" onClick={() => navigate('/work-orders')} />
        <StatCard label={t('workOrders.statusInProgress')} value={workOrders === null ? undefined : woInProgress.length} icon={IconTool} color="#3b82f6" onClick={() => navigate('/work-orders')} />
        <StatCard label={t('dashboard.woUrgent', 'Urgent')} value={workOrders === null ? undefined : woUrgent.length} icon={IconFlame} color="#ef4444" onClick={() => navigate('/work-orders')} />
        <StatCard label={t('workOrders.statusAll')} value={workOrders?.length} icon={IconTicket} color="#64748b" onClick={() => navigate('/work-orders')} />
      </SimpleGrid>

      <Text style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, letterSpacing: '0.5px', textTransform: 'uppercase', marginBottom: 10 }}>
        {t('facility.sectionHkOrder', 'HK Orders')}
      </Text>
      <SimpleGrid cols={{ base: 2, md: 4 }} mb="xl">
        <StatCard label={t('workOrders.statusOpen')} value={hkOrders === null ? undefined : hkoOpen.length} icon={IconBottle} color="#f59e0b" onClick={() => navigate('/hk-orders')} />
        <StatCard label={t('workOrders.statusInProgress')} value={hkOrders === null ? undefined : hkoInProgress.length} icon={IconTool} color="#3b82f6" onClick={() => navigate('/hk-orders')} />
        <StatCard label={t('dashboard.hkoUrgent', 'Urgent')} value={hkOrders === null ? undefined : hkoUrgent.length} icon={IconFlame} color="#ef4444" onClick={() => navigate('/hk-orders')} />
        <StatCard label={t('workOrders.statusAll')} value={hkOrders?.length} icon={IconBottle} color="#64748b" onClick={() => navigate('/hk-orders')} />
      </SimpleGrid>

      <SimpleGrid cols={{ base: 1, md: 2 }} spacing={isMobile ? 'md' : 'xl'}>
        <OpenListCard title={t('facility.openWorkOrders', 'Open Work Orders')} items={workOrders === null ? null : woOpen}
          emptyText={t('facility.noOpenWorkOrders', 'No open work orders')} icon={IconTicket} onNavigate={() => navigate('/work-orders')} />
        <OpenListCard title={t('facility.openHkOrders', 'Open HK Orders')} items={hkOrders === null ? null : hkoOpen}
          emptyText={t('facility.noOpenHkOrders', 'No open HK orders')} icon={IconBottle} onNavigate={() => navigate('/hk-orders')} />
      </SimpleGrid>

      {workOrders === null || hkOrders === null
        ? <Skeleton height={240} mt="xl" />
        : <OrderSummarySection workOrders={workOrders} hkOrders={hkOrders} />}
    </Box>
  )
}
